import type { FC } from 'react'

import { Checkbox, Input, ErrorMessage } from '@skytech/ui'
import type { FieldProps, FormikProps } from 'formik'
import { Field, FieldArray, useFormikContext } from 'formik'
import BellRingOutlineIcon from 'mdi-react/BellRingOutlineIcon'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'

import type { GroupedReminders } from '../helpers'

import { TimelineItem } from './TimelineItem'

import type { LeasingSettingsFormFields } from '..'

export const reminderMaxValue = 60

export const Reminders: FC<{ isDisabled?: boolean }> = ({ isDisabled = false }) => {
  const { values } = useFormikContext<LeasingSettingsFormFields>()
  const translate = useTranslation()

  const isPrevSetToOneDay = (index: number) => {
    if (index === 0) {
      return false
    }
    return values.reminders.slice(0, index).some(v => v.offsetDays === 1)
  }

  const isTimelineActive = (
    value: number | null,
    types: Array<{ id: number | null; type: 'EMAIL' | 'SMS'; value: boolean }>
  ) => Boolean(values.endOfLeaseIsActive && value && value > 0) && types.some(t => t.value)

  const isNextReminderVisible = (prevReminder: GroupedReminders): boolean =>
    prevReminder?.offsetDays !== null &&
    Number(prevReminder?.offsetDays) > 1 &&
    prevReminder?.types.some(t => t.value)

  const isNextRemindersNotEmpty = (currentIndex: number, reminders: GroupedReminders[]): boolean =>
    reminders.slice(currentIndex).some(v => Number(v.offsetDays) >= 1)

  const clearLastReminderValues = (idx: number, form: FormikProps<any>) => {
    form.setFieldValue(`reminders[${idx}].offsetDays`, null)
    form.setFieldValue(`reminders[${idx}].types[0].value`, false) // Default to false for EMAIL
    form.setFieldValue(`reminders[${idx}].types[1].value`, false) // Default to false for SMS
  }

  const handleClearReminder = (fromIdx: number, form: FormikProps<any>) => {
    const lastIdx = values.reminders.length - 1 // 4
    for (let i = fromIdx; i < lastIdx; i++) {
      const src = values.reminders[i + 1]
      form.setFieldValue(`reminders[${i}].offsetDays`, src.offsetDays)
      src.types.forEach((type, typeIdx) => {
        form.setFieldValue(`reminders[${i}].types[${typeIdx}].value`, type.value)
      })
    }
    clearLastReminderValues(lastIdx, form)
  }

  return (
    <FieldArray name="reminders">
      {() => (
        <>
          {values.reminders.map((reminder, idx) => {
            const title = `${translate('Reminder')} ${reminder.id}${reminder.id === 1 ? '*' : ''}`
            const isVisible =
              isNextRemindersNotEmpty(idx, values.reminders) ||
              idx === 0 ||
              isNextReminderVisible(values.reminders[idx - 1])

            if (!isVisible) return null

            return (
              <Field key={reminder.id} name={`reminders[${idx}].offsetDays`} required={idx === 0}>
                {({ field, meta, form }: FieldProps) => (
                  <TimelineItem
                    isRightError={true}
                    title={title}
                    icon={<BellRingOutlineIcon size={16} />}
                    meta={meta}
                    isActive={isTimelineActive(
                      field.value as number | null,
                      values.reminders[idx].types
                    )}
                    clearBtnOptions={{
                      hasButton:
                        reminder.id !== 1 &&
                        ((field.value && field.value > 0) ||
                          values.reminders[idx].types.some(t => t.value)),
                      disabled: isDisabled || !values.endOfLeaseIsActive,
                      onBtnClick: () => {
                        handleClearReminder(idx, form)
                      },
                    }}
                    checkboxComponent={
                      <CheckboxItems
                        idx={idx}
                        reminderTypes={values.reminders[idx].types}
                        isDisabled={
                          isDisabled || !values.endOfLeaseIsActive || isPrevSetToOneDay(idx)
                        }
                      />
                    }
                  >
                    <Input
                      type="number"
                      id={`reminders[${idx}].offsetDays`}
                      className="relative w-24 p-2"
                      max={
                        idx === 0
                          ? String(reminderMaxValue)
                          : String(values.reminders[idx - 1].offsetDays)
                      }
                      min="1"
                      isDisabled={
                        isDisabled || !values.endOfLeaseIsActive || isPrevSetToOneDay(idx)
                      }
                      touched={meta.touched}
                      error={meta.error ? ' ' : undefined}
                      errorTextClassName="m-0.5"
                      {...field}
                      value={field.value ?? ''}
                      onChange={e => {
                        form.setFieldValue(field.name, e.target.value)
                        form.setFieldTouched(`reminders[${idx}].types`, true)
                      }}
                    />
                  </TimelineItem>
                )}
              </Field>
            )
          })}
        </>
      )}
    </FieldArray>
  )
}

const CheckboxItems: FC<{
  idx: number
  reminderTypes: Array<{ id: number | null; type: 'EMAIL' | 'SMS'; value: boolean }>
  isDisabled?: boolean
}> = ({ idx, reminderTypes, isDisabled }) => (
  <Field name={`reminders[${idx}].types`}>
    {({ meta }: FieldProps) => (
      <div className="flex flex-col gap-2">
        <div className="flex flex-row gap-2">
          {reminderTypes[0].type === 'EMAIL' && (
            <Field name={`reminders[${idx}].types[0].value`}>
              {({ field }: FieldProps) => (
                <Checkbox
                  id={`reminders[${idx}].types[0].value`}
                  isDisabled={isDisabled}
                  label={<Translation id="EMAIL" />}
                  checked={field.value}
                  {...field}
                />
              )}
            </Field>
          )}
          {reminderTypes[1].type === 'SMS' && (
            <Field name={`reminders[${idx}].types[1].value`}>
              {({ field }: FieldProps) => (
                <Checkbox
                  id={`reminders[${idx}].types[1].value`}
                  isDisabled={isDisabled}
                  label={<Translation id="SMS" />}
                  checked={field.value}
                  {...field}
                />
              )}
            </Field>
          )}
        </div>
        {meta?.error && meta.touched && <ErrorMessage className="m-0">{meta.error}</ErrorMessage>}
      </div>
    )}
  </Field>
)
